<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import { type Column, type SlickGrid } from 'slickgrid';
import {
  BDropdown,
  BDropdownForm,
  BFormCheckbox,
  BButton,
} from 'bootstrap-vue-next';
import { isSystemColumn } from './helpers';

const props = defineProps({
  grid: { type: Object as PropType<SlickGrid<any>>, required: true },
  columns: { type: Array as PropType<Column<any>[]>, required: true },
  messages: { type: Object, required: true },
});

interface DisplayItem {
  name: any;
  show: boolean;
  visible: boolean;
  column: Column<any>;
}

const dropdownOpen = ref(false);
const localItems = ref<DisplayItem[]>([]);

const checkboxItems = computed(() => {
  return localItems.value.filter((item) => item.show);
});

const getItems = (): DisplayItem[] => {
  const gridColumns = props.grid.getColumns();

  return gridColumns.map((col) => ({
    name: col.name,
    show: !isSystemColumn(col),
    visible: !col.hidden,
    column: col,
  }));
};

const onShow = () => {
  localItems.value = getItems();
};

const closeDropdown = () => {
  dropdownOpen.value = false;
};

const applyChanges = () => {
  for (const item of localItems.value) {
    item.column.hidden = !item.visible;
  }

  props.grid.updateColumns();
  closeDropdown();
};
</script>

<template>
  <BDropdown
    v-model="dropdownOpen"
    size="sm"
    variant="light"
    toggle-class="border"
    class="slickg__column-selector"
    auto-close="outside"
    @show="onShow"
  >
    <template #button-content>
      <i
        class="fa fa-cog"
        aria-hidden="true"
        :title="messages.column_selector_button_text"
      />
    </template>

    <BDropdownForm>
      <div
        class="slickg__column-selector__options"
        style="max-height: 300px; overflow-y: auto"
      >
        <BFormCheckbox
          v-for="(item, index) in checkboxItems"
          :key="index"
          v-model="item.visible"
          :name="String(item.name)"
          class="text-nowrap"
        >
          {{ item.name }}
        </BFormCheckbox>
      </div>

      <div class="d-flex justify-content-end gap-2">
        <BButton variant="secondary" size="sm" @click="closeDropdown">
          {{ messages.column_selector_close_text }}
        </BButton>
        <BButton variant="primary" size="sm" @click="applyChanges">
          {{ messages.column_selector_apply_text }}
        </BButton>
      </div>
    </BDropdownForm>
  </BDropdown>
</template>
